import { useTranslation } from 'react-i18next';
import {
  Button,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  WorkspaceTable,
  type WorkspaceTableColumn,
} from '../../../ui/index.js';
import type { RequestView } from '../types.js';
import { RequestListRow } from './RequestListRow.js';
import { RequestMobileCard } from './RequestMobileCard.js';
import type { RequestsBatchExportController } from '../selection/useRequestSelection.js';

// WHY: SLA column stays 204px so worst-case Russian primary
// strings (`requests:format.completedIn` / `requests:format.overdue`
// with 3-digit-hour values) fit on one line at 12px tabular-nums
// after accounting for the 10px-each-side cell padding. The seven fixed
// columns total 788px; Name is the only flexible column.
const GRID_TEMPLATE = '100px minmax(300px,1fr) 48px 110px 90px 112px 204px 124px';
const SELECTION_GRID_TEMPLATE = `40px ${GRID_TEMPLATE}`;

// WHY: `WorkspaceTable` renders its children straight into a `display: grid`,
// so a state wrapper without the span collapses into the first column track.
const STATE_SPAN = { gridColumn: '1 / -1' } as const;

export function RequestsListTable(props: {
  visible: RequestView[];
  now: number;
  narrow: boolean;
  selectedId: string | null;
  isManagerValidating: boolean;
  isError: boolean;
  isInitialLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onOpenRow: (r: RequestView) => void;
  onLoadMore: () => void;
  unreadFor: (id: string) => { unreadCount: number; isMarkedUnread: boolean };
  notificationCountFor: (id: string) => number;
  batchExport?: Pick<
    RequestsBatchExportController,
    'selectionMode' | 'selectedRequestIds' | 'toggle'
  >;
}) {
  const { t } = useTranslation(['requests', 'common']);
  const {
    visible,
    now,
    narrow,
    selectedId,
    isManagerValidating,
    isError,
    isInitialLoading,
    hasNextPage,
    isFetchingNextPage,
    onOpenRow,
    onLoadMore,
    unreadFor,
    notificationCountFor,
    batchExport,
  } = props;
  const selectionMode = batchExport?.selectionMode ?? false;
  const selectedRequestIds = batchExport?.selectedRequestIds ?? new Set<string>();
  const dataColumns: readonly WorkspaceTableColumn[] = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: t('requests:list.columns.name') },
    {
      key: 'messages',
      label: (
        <span
          role="img"
          aria-label={t('requests:list.columns.messages')}
          title={t('requests:list.columns.messages')}
          style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
        >
          <Icon name="message-square" size={12} />
        </span>
      ),
    },
    { key: 'status', label: t('requests:list.columns.status') },
    { key: 'answers', label: t('list.columns.answers') },
    { key: 'cargo', label: t('list.columns.cargo'), align: 'right' },
    { key: 'deadlines', label: t('list.columns.deadlines') },
    { key: 'manager', label: t('list.columns.manager') },
  ];
  const columns: readonly WorkspaceTableColumn[] = selectionMode
    ? [
        {
          key: 'selection',
          label: (
            <span
              role="img"
              aria-label={t('requests:export.bulk.selectColumn')}
              title={t('requests:export.bulk.selectColumn')}
            >
              <Icon name="check" size={12} />
            </span>
          ),
        },
        ...dataColumns,
      ]
    : dataColumns;

  return (
    <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
      {!narrow && (
        <WorkspaceTable
          columnsTemplate={selectionMode ? SELECTION_GRID_TEMPLATE : GRID_TEMPLATE}
          columns={columns}
        >
          {visible.map((r) => (
            <RequestListRow
              key={r.id}
              r={r}
              now={now}
              selected={selectionMode ? selectedRequestIds.has(r.id) : r.id === selectedId}
              selectionMode={selectionMode}
              selectionChecked={selectedRequestIds.has(r.id)}
              onSelect={() => (selectionMode ? batchExport?.toggle(r, false) : onOpenRow(r))}
              onToggleSelection={(shiftKey) => batchExport?.toggle(r, shiftKey)}
              unread={unreadFor(r.id)}
              notificationCount={notificationCountFor(r.id)}
            />
          ))}

          {visible.length === 0 && isError && (
            <div data-testid="requests-list-error" role="alert" style={STATE_SPAN}>
              <ErrorState title={t('requests:list.error')} />
            </div>
          )}
          {visible.length === 0 && !isError && (isManagerValidating || isInitialLoading) && (
            <div data-testid="requests-list-loading" style={STATE_SPAN}>
              <LoadingState text={t('requests:list.loading')} />
            </div>
          )}
          {visible.length === 0 && !isError && !isManagerValidating && !isInitialLoading && (
            <div data-testid="requests-list-empty" style={STATE_SPAN}>
              <EmptyState icon="inbox" title={t('requests:list.empty')} />
            </div>
          )}
        </WorkspaceTable>
      )}
      {narrow && (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: '12px 12px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {visible.map((r) => (
            <li key={r.id} style={{ listStyle: 'none' }}>
              <RequestMobileCard
                r={r}
                now={now}
                selected={selectionMode ? selectedRequestIds.has(r.id) : r.id === selectedId}
                selectionMode={selectionMode}
                selectionChecked={selectedRequestIds.has(r.id)}
                onSelect={() => (selectionMode ? batchExport?.toggle(r, false) : onOpenRow(r))}
                onToggleSelection={(shiftKey) => batchExport?.toggle(r, shiftKey)}
                unread={unreadFor(r.id)}
                notificationCount={notificationCountFor(r.id)}
              />
            </li>
          ))}
          {visible.length === 0 && isError && (
            <li style={{ listStyle: 'none' }}>
              <div data-testid="requests-list-error" role="alert">
                <ErrorState title={t('requests:list.error')} />
              </div>
            </li>
          )}
          {visible.length === 0 && !isError && (isManagerValidating || isInitialLoading) && (
            <li style={{ listStyle: 'none' }}>
              <div data-testid="requests-list-loading">
                <LoadingState text={t('requests:list.loading')} />
              </div>
            </li>
          )}
          {visible.length === 0 && !isError && !isManagerValidating && !isInitialLoading && (
            <li style={{ listStyle: 'none' }}>
              <div data-testid="requests-list-empty">
                <EmptyState icon="inbox" title={t('requests:list.empty')} />
              </div>
            </li>
          )}
        </ul>
      )}

      {hasNextPage && (
        <div
          style={{
            padding: '14px 24px 22px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            kind="secondary"
            size="sm"
            data-testid="requests-load-more"
            disabled={isFetchingNextPage}
            onClick={onLoadMore}
          >
            {isFetchingNextPage ? t('requests:list.loadingMore') : t('requests:list.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
